import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Businesshours } from '@gql/base-types';
import type { TimeBlock as EditorTimeBlock } from '@components/Schedule/ScheduleEditor.types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export type TimeBlock = {
  id: string;
  openTime: string | null;
  closedTime: string | null;
  isDefault: boolean;
};

export type ProcessedBusinessHour = {
  dayOfWeek: number;
  dayName: string;
  timeBlocks: TimeBlock[];
  isOpen: boolean;
  type: string;
};

export type ProcessedClosure = {
  id: string;
  name: string;
  date: string;
  type: string;
  isRecurring: boolean;
  rawData: Businesshours;
};

/**
 * Calculate actual date from recurring pattern (e.g., "First Monday of January")
 */
export const getDateFromComponents = (
  dayOfWeek: number,
  weekOfMonth: number,
  month: number,
  year?: number
): string => {
  const targetYear = year || dayjs().year();
  const targetMonth = month - 1; // Convert to 0-indexed

  // Start at first day of target month
  const firstOfMonth = dayjs().year(targetYear).month(targetMonth).date(1);

  // Find first occurrence of the target day of week
  let firstOccurrence = firstOfMonth.day(dayOfWeek);

  // If the first occurrence is in the previous month, move to next week
  if (firstOccurrence.month() !== targetMonth) {
    firstOccurrence = firstOccurrence.add(1, 'week');
  }

  // Add weeks to get to the target week
  const targetDate = firstOccurrence.add(weekOfMonth - 1, 'week');

  return targetDate.format('MMM DD, YYYY');
};

/**
 * Format time in 12-hour format with AM/PM
 * Database times are stored as local time strings in the location's timezone
 */
export const formatTime = (time: string | null, tz?: string): string => {
  if (!time) return '';

  // Parse time (format: HH:mm:ss or HH:mm)
  const today = dayjs().format('YYYY-MM-DD');

  if (tz) {
    // Parse the time directly IN the specified timezone (not FROM local timezone)
    return dayjs.tz(`${today} ${time}`, tz).format('h:mm A');
  }

  return dayjs(`${today} ${time}`).format('h:mm A');
};

/**
 * Format time range for display
 */
export const formatTimeRange = (
  openTime: string | null,
  closedTime: string | null,
  tz?: string
): string => {
  if (!openTime || !closedTime) return 'Closed';
  return `${formatTime(openTime, tz)} - ${formatTime(closedTime, tz)}`;
};

/**
 * Convert dayjs time to database format (HH:mm:ss as local time)
 * Database stores times as local time strings, not UTC
 * The timezone parameter is kept for API consistency but times are stored as-is
 */
export const timeToDbFormat = (time: dayjs.Dayjs | null, _tz?: string): string | null => {
  if (!time || !time.isValid()) return null;

  // Store the time as-is in HH:mm:ss format
  // The timezone context is maintained by the location's timezone field
  return time.format('HH:mm:ss');
};

/**
 * Convert database time to dayjs object for today
 * Database times are stored as local time strings (e.g., "09:00:00" means 9 AM local time)
 * We need to create a dayjs object in the specified timezone
 */
export const dbTimeTodayjs = (time: string | null, tz?: string): dayjs.Dayjs | null => {
  if (!time) return null;
  const today = dayjs().format('YYYY-MM-DD');

  if (tz) {
    // Parse the time in the specified timezone
    return dayjs.tz(`${today} ${time}`, tz);
  }

  return dayjs(`${today} ${time}`);
};

/**
 * Sort business hours by day of week, then by open time
 */
export const sortBusinessHours = (hours: Businesshours[]): Businesshours[] => {
  return [...hours].sort((a, b) => {
    // First sort by day_of_week
    const dayA = a.day_of_week ?? 999;
    const dayB = b.day_of_week ?? 999;

    if (dayA !== dayB) {
      return dayA - dayB;
    }

    // If same day, sort by open_time (nulls last)
    if (!a.open_time) return 1;
    if (!b.open_time) return -1;

    // Compare times
    const timeA = dayjs(`2000-01-01 ${a.open_time}`);
    const timeB = dayjs(`2000-01-01 ${b.open_time}`);

    return timeA.diff(timeB);
  });
};

/**
 * Process and merge business hours with defaults
 * Returns hours grouped by day with multiple time blocks per day
 */
export const processBusinessHours = (
  businessHours: Businesshours[],
  locationId: string
): ProcessedBusinessHour[] => {
  const locationSpecific = businessHours.filter(
    (h) => h.location_id?.toString() === locationId && h.type === 'business-hours'
  );

  const defaults = businessHours.filter(
    (h) => !h.location_id && h.type === 'default-hours'
  );

  const processed: ProcessedBusinessHour[] = [];

  // Process each day of the week
  for (let day = 0; day <= 6; day++) {
    // Check for location-specific hours first
    const locationHours = locationSpecific.filter((h) => h.day_of_week === day);

    if (locationHours.length > 0) {
      // Use location-specific hours - multiple blocks possible
      const sortedHours = sortBusinessHours(locationHours);
      const timeBlocks: TimeBlock[] = sortedHours.map((h) => ({
        id: h.id.toString(),
        openTime: h.open_time || null,
        closedTime: h.closed_time || null,
        isDefault: false,
      }));

      const hasOpenBlock = sortedHours.some((h) => h.is_open);

      processed.push({
        dayOfWeek: day,
        dayName: DAY_NAMES[day],
        timeBlocks,
        isOpen: hasOpenBlock,
        type: 'business-hours',
      });
    } else {
      // Fall back to default hours
      const defaultHours = defaults.filter((h) => h.day_of_week === day);

      if (defaultHours.length > 0) {
        const sortedHours = sortBusinessHours(defaultHours);
        const timeBlocks: TimeBlock[] = sortedHours.map((h) => ({
          id: h.id.toString(),
          openTime: h.open_time || null,
          closedTime: h.closed_time || null,
          isDefault: true,
        }));

        const hasOpenBlock = sortedHours.some((h) => h.is_open);

        processed.push({
          dayOfWeek: day,
          dayName: DAY_NAMES[day],
          timeBlocks,
          isOpen: hasOpenBlock,
          type: 'default-hours',
        });
      } else {
        // No hours defined for this day (treat as closed)
        processed.push({
          dayOfWeek: day,
          dayName: DAY_NAMES[day],
          timeBlocks: [],
          isOpen: false,
          type: 'default-hours',
        });
      }
    }
  }

  return processed;
};

/**
 * Process closures (one-off and recurring)
 */
export const processClosures = (businessHours: Businesshours[]): ProcessedClosure[] => {
  const closures = businessHours.filter(
    (h) => h.type === 'one-off' || h.type === 'customer-holiday'
  );

  return closures.map((closure) => {
    let date = '';
    let isRecurring = false;

    if (closure.type === 'one-off' && closure.day && closure.month && closure.year) {
      // Specific date
      date = dayjs()
        .year(closure.year)
        .month(closure.month - 1)
        .date(closure.day)
        .format('MMM DD, YYYY');
      isRecurring = false;
    } else if (
      closure.type === 'customer-holiday' &&
      closure.day_of_week != null &&
      closure.week_of_month &&
      closure.month
    ) {
      // Recurring date
      date = getDateFromComponents(
        closure.day_of_week,
        closure.week_of_month,
        closure.month
      );
      isRecurring = true;
    }

    return {
      id: closure.id.toString(),
      name: closure.name || 'Unnamed Closure',
      date,
      type: closure.type,
      isRecurring,
      rawData: closure,
    };
  });
};

/**
 * Process HopDrive holidays (read-only)
 */
export const processHopDriveHolidays = (businessHours: Businesshours[]): ProcessedClosure[] => {
  const holidays = businessHours.filter(
    (h) => h.type === 'hopdrive-holiday' || h.type === 'federal-holiday'
  );

  return holidays.map((holiday) => {
    let date = '';

    if (holiday.day && holiday.month) {
      const year = holiday.year ?? dayjs().year();
      date = dayjs()
        .year(year)
        .month(holiday.month - 1)
        .date(holiday.day)
        .format('MMM DD, YYYY');
    }

    return {
      id: holiday.id.toString(),
      name: holiday.name || 'Company Holiday',
      date,
      type: holiday.type,
      isRecurring: !holiday.year, // No year means recurring
      rawData: holiday,
    };
  });
};

/**
 * Validate time range
 */
export const validateTimeRange = (
  openTime: dayjs.Dayjs | null,
  closedTime: dayjs.Dayjs | null
): { valid: boolean; error?: string } => {
  if (!openTime || !closedTime) {
    return { valid: false, error: 'Both open and close times are required' };
  }

  if (!openTime.isValid() || !closedTime.isValid()) {
    return { valid: false, error: 'Invalid time format' };
  }

  if (closedTime.isBefore(openTime) || closedTime.isSame(openTime)) {
    return { valid: false, error: 'Close time must be after open time' };
  }

  return { valid: true };
};

/**
 * Merge overlapping time blocks within a single day (interval merge).
 *
 * A day legitimately supports multiple *disjoint* blocks — e.g. a split shift
 * open 8–11 AM and again 3–6 PM. Those must be preserved. What is NOT valid is
 * two blocks whose ranges overlap (or touch); those are collapsed into one.
 *
 * Algorithm: sort by open time, then walk left→right keeping a "current"
 * cluster. If the next block starts at or before the current cluster's close
 * (overlap OR touching boundary), merge it in — keeping the earliest open and
 * the latest (max) close of the cluster. Otherwise the next block starts a new
 * cluster. This is per-cluster, so disjoint blocks stay separate; only the
 * globally-earliest-open / globally-latest-close would be wrong here.
 *
 * id survival: at most one original DB id survives per cluster — the earliest
 * block in the cluster that already has an id. That surviving id flows through
 * handleSave as an UPDATE (min open / max close). Every other id in the cluster
 * drops out of the block set and is hard-deleted by handleSave's existing diff.
 *
 * Times are compared via `timeToDbFormat` (HH:mm:ss). This is timezone-safe:
 * every block in a day shares the location timezone, and zero-padded 24h
 * strings sort lexically in chronological order.
 */
export const mergeTimeBlocks = (
  blocks: EditorTimeBlock[],
  tz?: string
): EditorTimeBlock[] => {
  // Drop blocks we can't reason about (missing/invalid times) — return as-is.
  const valid = blocks.filter(
    (b) => b.openTime && b.closedTime && b.openTime.isValid() && b.closedTime.isValid()
  );
  if (valid.length <= 1) return valid.map((b) => ({ ...b }));

  const sorted = [...valid].sort((a, b) =>
    (timeToDbFormat(a.openTime, tz) ?? '').localeCompare(timeToDbFormat(b.openTime, tz) ?? '')
  );

  const merged: EditorTimeBlock[] = [];
  for (const block of sorted) {
    const current = merged[merged.length - 1];
    if (!current) {
      merged.push({ ...block });
      continue;
    }

    const currClose = timeToDbFormat(current.closedTime, tz) ?? '';
    const nextOpen = timeToDbFormat(block.openTime, tz) ?? '';

    if (nextOpen.localeCompare(currClose) <= 0) {
      // Overlap or touching -> merge into the current cluster.
      const nextClose = timeToDbFormat(block.closedTime, tz) ?? '';
      if (nextClose.localeCompare(currClose) > 0) {
        current.closedTime = block.closedTime; // extend to the later close
      }
      // Keep an existing DB id so the surviving block becomes an UPDATE, not a
      // delete+recreate. The earliest id in the cluster wins.
      if (!current.id && block.id) current.id = block.id;
      current.isDefault = false;
    } else {
      // Disjoint (a real gap) -> start a new cluster.
      merged.push({ ...block });
    }
  }

  return merged;
};
